import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Target, TrendingUp, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Home = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Mock submission - will be replaced with backend
    setTimeout(() => {
      toast({
        title: "Thank you for your interest!",
        description: "We'll be in touch soon to discuss how we can help transform your business.",
      });
      setEmail('');
      setLoading(false);
    }, 1000);
  };

  const services = [
    {
      icon: <Zap className="service-icon" />,
      title: "Workflow Automation",
      description: "Streamline repetitive tasks and free your team to focus on what matters most—growing your business."
    },
    {
      icon: <Target className="service-icon" />,
      title: "Process Auditing",
      description: "Identify inefficiencies and bottlenecks with our comprehensive process analysis and optimization strategies."
    },
    {
      icon: <TrendingUp className="service-icon" />,
      title: "AI Consultation",
      description: "Get expert guidance on implementing AI solutions tailored to your unique business challenges and goals."
    }
  ];

  const benefits = [
    "Reduce operational costs by up to 60%",
    "Increase productivity and output quality",
    "Scale your operations without proportional cost increases",
    "Stay competitive in the digital transformation era"
  ];

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Empowering SMEs to <span className="text-accent">Scale & Thrive</span> in the AI Era
            </h1>
            <p className="hero-subtitle">
              Transform your business operations with intelligent automation. We help ambitious small and medium enterprises catch up and excel in today's rapidly evolving technological landscape.
            </p>
            <div className="hero-cta-group">
              <Link to="/contact" className="btn-primary">
                Get Free Consultation
                <ArrowRight size={20} />
              </Link>
              <Link to="/services#build-agent" className="btn-secondary">
                Build AI Agent
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
          <div className="hero-image">
            <img 
              src="https://images.unsplash.com/photo-1694903089438-bf28d4697d9a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGF1dG9tYXRpb258ZW58MHx8fHwxNzc0MDgzMTg2fDA&ixlib=rb-4.1.0&q=85" 
              alt="AI Automation Technology"
              className="hero-img"
            />
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="services-overview">
        <div className="section-header">
          <h2 className="section-title">How We Help You Scale</h2>
          <p className="section-subtitle">
            Comprehensive solutions designed to transform your business operations
          </p>
        </div>
        <div className="services-grid">
          {services.map((service, index) => (
            <div key={index} className="service-card">
              <div className="service-icon-wrapper">
                {service.icon}
              </div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.description}</p>
              <Link to="/services" className="service-link">
                Learn More <ArrowRight size={16} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="benefits-content">
          <div className="benefits-text">
            <h2 className="benefits-title">
              Why Choose <span className="text-accent">NTG</span>?
            </h2>
            <p className="benefits-subtitle">
              We understand the challenges SMEs face in adopting new technologies. Our mission is to make AI-driven automation accessible, affordable, and impactful for businesses ready to grow.
            </p>
            <ul className="benefits-list">
              {benefits.map((benefit, index) => (
                <li key={index} className="benefit-item">
                  <CheckCircle className="benefit-icon" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
            <Link to="/case-studies" className="btn-primary">
              View Success Stories
              <ArrowRight size={20} />
            </Link>
          </div>
          <div className="benefits-image">
            <img 
              src="https://images.unsplash.com/photo-1770210217380-d78a69acdc77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxBSSUyMGF1dG9tYXRpb24lMjB0ZWNobm9sb2d5JTIwYnVzaW5lc3N8ZW58MHx8fHwxNzc0MDgzMTcxfDA&ixlib=rb-4.1.0&q=85" 
              alt="AI Technology"
              className="benefits-img"
            />
          </div>
        </div>
      </section>

      {/* Email Signup Section */}
      <section className="email-signup-section">
        <div className="email-signup-content">
          <h2 className="email-signup-title">Ready to Transform Your Business?</h2>
          <p className="email-signup-subtitle">
            Get a free process audit and discover automation opportunities in your business.
          </p>
          <form onSubmit={handleEmailSubmit} className="email-signup-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your work email"
              required
              className="email-input"
            />
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Submitting...' : 'Request Audit'}
              <ArrowRight size={20} />
            </button>
          </form>
          <p className="email-signup-note">
            No spam, ever. We respect your privacy.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
